import classNames from "classnames";
import { EMPTY_ARRAY, setTrackingId } from "client/Misc/utils";
import {
	forwardRef,
	ReactElement,
	Ref,
	useContext,
	useMemo
} from "react";
import { PrimaryButtonColor } from "../Button/Button";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
 } from "../ControlAttachment";
import {
	FontIconName,
	IconValue,
	SvgIconValue,
	unsetIconColor
 } from "../Icon/Icon";
import { kindToHoverStyleMapping } from "./common";
import {
	ExplicitInheritedTransparentColoredIconValue,
	IconButtonHoverStyle,
	IconButtonKind,
	IconButtonShape,
	IconButtonSize,
	isExplicitInheritedTransparentColoredIconValue
} from "./IconButton";
import styles from "./IconButton.module.less";
import { IconButtonRenderer } from "./IconButtonRenderer";

export type IconButtonLikeProps = {
	trackingId: string | undefined;
	hasNoBorderRadius?: boolean;
	size?: IconButtonSize;
	shape?: IconButtonShape;
	height?: "auto" | "fill";
	width?: "auto" | "fill";
	isDisabled?: boolean;
	hoverStyle?: IconButtonHoverStyle;
	attachedOn?: ControlAttachmentOptions;
	tooltip?: string;
	isActive?: boolean;
	isPlaceholder?: boolean;
} & (
	{
		kind: Exclude<IconButtonKind, "transparent-colored" | "primary">;
		icon: FontIconName | SvgIconValue;
	}
	|
	{
		kind: "transparent-colored";
		icon: IconValue | ExplicitInheritedTransparentColoredIconValue;
	}
	|
	{
		kind: "primary";
		icon: IconValue;
		color?: PrimaryButtonColor;
	}
);

// This component exist for places were we want to have UI/UX of the IconButton but cannot actually
// use a real button (e.g. when you are already inside a button)
export const IconButtonLike = forwardRef(
	(props: IconButtonLikeProps, ref: Ref<HTMLElement>): ReactElement => {
		const {
			trackingId,
			hasNoBorderRadius = false,
			kind,
			size = "normal",
			shape = "square",
			height = "auto",
			width = "auto",
			isDisabled,
			icon: iconFromProps,
			hoverStyle = "default",
			attachedOn = EMPTY_ARRAY,
			tooltip,
			isActive = false,
			isPlaceholder = false
		} = props;

		const {
			attachedOn: attachmentsFromContext
		} = useContext(ControlAttachmentContext);

		const icon = useMemo(
			(): IconValue => {
				let result: IconValue | ExplicitInheritedTransparentColoredIconValue = iconFromProps;

				if (isExplicitInheritedTransparentColoredIconValue(result)) {
					result = result.name;
				}

				if (isDisabled) {
					result = unsetIconColor(result);
				}

				return result;
			},
			[iconFromProps, isDisabled]
		);

		const attachedOnClassNames: string = getAttachmentClassNames(attachedOn, attachmentsFromContext);

		return (
			<span
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_square]: shape === "square",
						[styles.root_noBorderRadius]: hasNoBorderRadius,
						[styles.root_fillHeight]: height === "fill",
						[styles.root_fillWidth]: width === "fill",
						[styles.root_large]: size === "large",
						[styles.root_placeholder]: isPlaceholder,
						[styles.root_active]: isActive,
						[styles.root_disabled]: isDisabled,
						[styles.root_inheritColor]: isExplicitInheritedTransparentColoredIconValue(iconFromProps)
					},
					kindToHoverStyleMapping[kind][hoverStyle],
					attachedOnClassNames
				)}
				title={tooltip}
				ref={ref}
			>
				<IconButtonRenderer
					kind={kind}
					color={props.kind === "primary" ? props.color : undefined}
					size={size}
					icon={icon}
					attachedOnClassNames={attachedOnClassNames}
				/>
			</span>
		);
	}
);
IconButtonLike.displayName = "IconButtonLike";
